import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ActivatedRoute, RouterLink, RouterLinkActive, RouterOutlet, Router } from '@angular/router';
import { ModalService } from '../../../../services/modal.service';
import { Observable, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { I18nTranslator } from '../../../../i18n/translate';
import {
  ARColzaAzoteFrequency,
  ARColzaAzoteProductType,
  ARColzaOrderType,
  ARColzaOrganicProductType,
} from '../../../../models/agrorendement-colza-lists.model';
import { ARColzaCampaign, ARColzaOrderItem, ARColzaParcel } from '../../../../models/agrorendement-colza.model';
import {
  ARConsoleFormatType,
  ARConsoleModulationType,
  ARConsoleType,
  ARConsoleVersion,
  ARFertilizerType,
} from '../../../../models/agrorendement-common-lists.model';
import {
  ARFertilizerData,
  ARFertilizerEHDataFacade,
  ARVraData,
  ARVraEHDataFacade,
  CbBatchEditEH,
} from '../../../../models/agrorendement-common.model';
import { ListItem } from '../../../../models/lists.model';
import { Organisation } from '../../../../models/organisation.model';
import { CampaignYearPipe } from '../../../../pipes/campaignYear.pipe';
import { CapitalizePipe } from '../../../../pipes/capitalize.pipe';
import { ARColzaListType, ARColzaListsService } from '../../../../services/agrorendement-colza-lists.service';
import { ARColzaService } from '../../../../services/agrorendement-colza.service';
import { BeApiService } from '../../../../services/beapi.service';
import { KeycloakService } from '../../../../services/keycloak.service';
import { ParcelService } from '../../../../services/parcel.service';
import { UserService } from '../../../../services/user.service';
import { Tooltip } from '../../../../utils/Tooltip';
import { TooltipContent } from '../../../../utils/TooltipContent';
import { getBatchOrderItems, prepareBatchSave } from '../../../../utils/batchedit.utils';
import { PreventDoubleClickDirective } from '../../../../utils/preventDoubleClick';
import { TOOLTIP_TEXTS } from '../../../../utils/tooltip-texts';
import {
  correctDate,
  isBooleanValue,
  showErrorMessage,
  sortingNumber,
  sortingString,
  toBoolean,
  toNumber,
} from '../../../../utils/utils';
import { ARFertilizerEditComponent } from '../common/fertilizer-edit.component';
import { ARVraEditComponent } from '../common/vra-edit.component';
import { ARColzaUtils } from './colza.utils';
import { TranslatePipe, TranslateWithPunctuationPipe } from '../../../../pipes/translate.pipe';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { FarmerAuthorizationsService } from '../../../../services/farmer-authorizations.service';
import { isFarmerActionForbidden } from '../../../../utils/farmer-action-error';
import { isExistingOrder, isOrderSaveForbidden } from '../../../../utils/order-save-forbidden';

@Component({
  standalone: true,
  selector: 'ar-colza-orderItem-edit',
  templateUrl: 'orderItem-edit.component.html',
  imports: [
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    Tooltip,
    TooltipContent,
    ARVraEditComponent,
    ARFertilizerEditComponent,
    PreventDoubleClickDirective,
    CampaignYearPipe,
    CapitalizePipe,
    TranslatePipe,
    TranslateWithPunctuationPipe,
    CommonModule,
    FormsModule,
    TooltipModule,
  ],
  providers: [ARColzaService, ParcelService, BeApiService, UserService],
})
export class ARColzaOrderItemEditComponent implements OnInit, OnDestroy {
  @Input() orderItem: ARColzaOrderItem;
  @Output() close = new EventEmitter();

  error: any;
  sub: any;

  @ViewChildren('fertilizerEditEH', { read: ARFertilizerEditComponent })
  fertilizerEditEHRef: QueryList<ARFertilizerEditComponent>;
  @ViewChildren('fertilizerEditSH', { read: ARFertilizerEditComponent })
  fertilizerEditSHRef: QueryList<ARFertilizerEditComponent>;
  @ViewChildren('vraEditEH', { read: ARVraEditComponent }) vraEditEHRef: QueryList<ARVraEditComponent>;
  @ViewChildren('vraEditSH', { read: ARVraEditComponent }) vraEditSHRef: QueryList<ARVraEditComponent>;
  @ViewChild('orderType') orderTypeControl: ElementRef;

  edited = false;
  withinOrder = false;

  orderItemId: number;
  parcel: ARColzaParcel;
  pastAzoteProductTypes: ARColzaAzoteProductType[];
  azoteProductTypes: ARColzaAzoteProductType[];
  azoteFrequencies: ARColzaAzoteFrequency[];
  orderTypes: ARColzaOrderType[];
  organicProductTypes: ARColzaOrganicProductType[];
  firstOrganicProductTypes: ARColzaOrganicProductType[];
  secondOrganicProductTypes: ARColzaOrganicProductType[];

  orderTypesIndex = new Map<number, string>();
  orderTypesReverseIndex = new Map<string, number>();
  productTypesIndex = new Map<number, string>();
  organicProductTypesIndex = new Map<number, string>();
  showFallFirstOrganicInformation: boolean;
  showFallSecondOrganicInformation: boolean;
  requireFallFirstOrganicInformation: boolean;
  requireFallSecondOrganicInformation: boolean;
  isZonageSH: boolean;
  isZonageEH: boolean;
  isZonageOrderType: boolean = null;

  requireReliquatSortieHiver = false;
  requireEpandageInfo: boolean = undefined;

  ehFertilizerData: ARFertilizerData;
  ehVraData: ARVraData;

  tooltipTexts = TOOLTIP_TEXTS;

  private adviceTypeLockThresholdMs = 3 * 24 * 60 * 60 * 1000;
  isAdviceTypeLocked = false;

  isBatch = false;
  batchOrderItems: ARColzaOrderItem[];
  cbBatchEdit: Object;
  cbBatchEditEh: Object = {};

  currentCampaign: ARColzaCampaign;
  isColzaEHEnded: boolean = false;

  showBeApi = false;
  hasBeApi = false;

  isAdmin = false;

  ehShLinks = new Map<string, boolean>();

  // Block 6 (C2027-196): whether the organisation lets this farmer-only user save this order.
  canCreateOrder = true;
  canEditOrder = true;
  saveForbidden = false;
  orderIsBeingEdited = false;

  constructor(
    private colzaService: ARColzaService,
    private colzaListService: ARColzaListsService,
    private parcelService: ParcelService,
    private beApiService: BeApiService,
    private userService: UserService,
    private i18nTranslator: I18nTranslator,
    private modal: ModalService,
    private router: Router,
    private route: ActivatedRoute,
    private farmerAuthorizationsService: FarmerAuthorizationsService,
  ) {
    this.isAdmin = KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN');
    this.farmerAuthorizationsService.can('createOrder').subscribe((allowed) => {
      this.canCreateOrder = allowed;
      this.updateSaveForbidden();
    });
    this.farmerAuthorizationsService.can('editOrder').subscribe((allowed) => {
      this.canEditOrder = allowed;
      this.updateSaveForbidden();
    });
  }

  updateSaveForbidden() {
    this.saveForbidden = isOrderSaveForbidden(
      this.isBatch,
      this.orderItem,
      this.batchOrderItems,
      this.canCreateOrder,
      this.canEditOrder,
    );
    this.orderIsBeingEdited = isExistingOrder(this.isBatch, this.orderItem, this.batchOrderItems);
  }

  goBack(savedConfig: ARColzaOrderItem = null) {
    this.close.emit(savedConfig);
    this.router.navigate(['/orders', 'azote', 'colza', 'parcels']);
    return false;
  }

  save() {
    // on nettoie sinon .id = null pas valide sur le serveur

    // EH
    let restoreEhFertilizerType = false;
    if (this.orderItem.ehFertilizerType && !this.orderItem.ehFertilizerType.id) {
      restoreEhFertilizerType = true;
      this.orderItem.ehFertilizerType = null;
    }
    let restoreEhConsoleModulationType = false;
    if (this.orderItem.ehConsoleModulationType && !this.orderItem.ehConsoleModulationType.id) {
      restoreEhConsoleModulationType = true;
      this.orderItem.ehConsoleModulationType = null;
    }
    let restoreEhConsoleFormatType = false;
    if (this.orderItem.ehConsoleFormatType && !this.orderItem.ehConsoleFormatType.id) {
      restoreEhConsoleFormatType = true;
      this.orderItem.ehConsoleFormatType = null;
    }
    let restoreEhConsoleType = false;
    if (this.orderItem.ehConsoleType && !this.orderItem.ehConsoleType.id) {
      restoreEhConsoleType = true;
      this.orderItem.ehConsoleType = null;
    }
    let restoreEhConsoleVersion = false;
    if (this.orderItem.ehConsoleVersion && !this.orderItem.ehConsoleVersion.id) {
      restoreEhConsoleVersion = true;
      this.orderItem.ehConsoleVersion = null;
    }

    // SH
    let restoreFertilizerType = false;
    if (!this.orderItem.fertilizerType.id) {
      restoreFertilizerType = true;
      this.orderItem.fertilizerType = null;
    }
    let restoreConsoleModulationType = false;
    if (!this.orderItem.consoleModulationType.id) {
      restoreConsoleModulationType = true;
      this.orderItem.consoleModulationType = null;
    }
    let restoreConsoleFormatType = false;
    if (!this.orderItem.consoleFormatType.id) {
      restoreConsoleFormatType = true;
      this.orderItem.consoleFormatType = null;
    }
    let restoreConsoleType = false;
    if (!this.orderItem.consoleType.id) {
      restoreConsoleType = true;
      this.orderItem.consoleType = null;
    }
    let restoreConsoleVersion = false;
    if (!this.orderItem.consoleVersion.id) {
      restoreConsoleVersion = true;
      this.orderItem.consoleVersion = null;
    }

    let restoreFallFirstOrganicProductType = false;
    if (!this.orderItem.fallFirstOrganicProductType.id) {
      restoreFallFirstOrganicProductType = true;
      this.orderItem.fallFirstOrganicProductType = null;
    }
    let restoreFallSecondOrganicProductType = false;
    if (!this.orderItem.fallSecondOrganicProductType.id) {
      restoreFallSecondOrganicProductType = true;
      this.orderItem.fallSecondOrganicProductType = null;
    }
    let restorePastAzoteProductType = false;
    if (!this.orderItem.pastAzoteProductType.id) {
      restorePastAzoteProductType = true;
      this.orderItem.pastAzoteProductType = null;
    }
    let restorePastAzoteFrequency = false;
    if (!this.orderItem.pastAzoteFrequency.id) {
      restorePastAzoteFrequency = true;
      this.orderItem.pastAzoteFrequency = null;
    }
    let restoreOrderType = false;
    if (!this.orderItem.orderType.id) {
      restoreOrderType = true;
      this.orderItem.orderType = null;
    }

    this.orderItem.objective = toNumber(this.orderItem.objective);
    this.orderItem.fallFirstOrganicConcentration = toNumber(this.orderItem.fallFirstOrganicConcentration);
    this.orderItem.fallFirstOrganicDensity = toNumber(this.orderItem.fallFirstOrganicDensity);
    this.orderItem.fallSecondOrganicConcentration = toNumber(this.orderItem.fallSecondOrganicConcentration);
    this.orderItem.fallSecondOrganicDensity = toNumber(this.orderItem.fallSecondOrganicDensity);
    this.orderItem.fertilizerConcentration = toNumber(this.orderItem.fertilizerConcentration);
    this.orderItem.reliquatSortieHiver = toNumber(this.orderItem.reliquatSortieHiver);

    this.orderItem.ehAzoteToSpread = toNumber(this.orderItem.ehAzoteToSpread);

    const noneProductId = Array.from(this.productTypesIndex.entries()).find((ks) => ks[1] === 'NONE')[0];

    if (
      this.orderItem.fallFirstOrganicProductType &&
      toNumber(this.orderItem.fallFirstOrganicProductType.id) === noneProductId
    ) {
      this.orderItem.fallSecondOrganicProductType = {
        id: noneProductId,
        code: 'NONE',
        label: '',
      };
      this.orderItem.fallSecondOrganicConcentration = undefined;
      this.orderItem.fallSecondOrganicDensity = undefined;
    }

    if (this.isBatch) {
      const obss: Observable<ARColzaOrderItem>[] = [];
      prepareBatchSave(this.orderItem, this.batchOrderItems, this.cbBatchEdit).forEach((batchOrderItem: any) => {
        obss.push(this.colzaService.saveOrderItem(batchOrderItem));
      });

      forkJoin(obss).subscribe(
        (orderItems) => {
          this.goBack();
        },
        (error) => this.handleSavebatchError(error),
      );
    } else {
      this.colzaService.saveOrderItem(this.orderItem).subscribe(
        (orderItem) => {
          if (!this.withinOrder) {
            this.goBack(orderItem);
          } else {
            this.confirmOrder(orderItem.parcel.id);
          }
        },
        (error) => this.handleSaveError(error),
      );
    }

    // sinon erreur de binding

    // EH

    if (restoreEhConsoleModulationType) {
      this.orderItem.ehConsoleModulationType = new ARConsoleModulationType();
      this.orderItem.ehConsoleModulationType.id = null;
    }
    if (restoreEhConsoleFormatType) {
      this.orderItem.ehConsoleFormatType = new ARConsoleFormatType();
      this.orderItem.ehConsoleFormatType.id = null;
    }
    if (restoreEhConsoleType) {
      this.orderItem.ehConsoleType = new ARConsoleType();
      this.orderItem.ehConsoleType.id = null;
    }
    if (restoreEhConsoleVersion) {
      this.orderItem.ehConsoleVersion = new ARConsoleVersion();
      this.orderItem.ehConsoleVersion.id = null;
    }
    if (restoreEhFertilizerType) {
      this.orderItem.ehFertilizerType = new ARFertilizerType();
      this.orderItem.ehFertilizerType.id = null;
    }

    // SH
    if (restoreConsoleModulationType) {
      this.orderItem.consoleModulationType = new ARConsoleModulationType();
      this.orderItem.consoleModulationType.id = null;
    }
    if (restoreConsoleFormatType) {
      this.orderItem.consoleFormatType = new ARConsoleFormatType();
      this.orderItem.consoleFormatType.id = null;
    }
    if (restoreConsoleType) {
      this.orderItem.consoleType = new ARConsoleType();
      this.orderItem.consoleType.id = null;
    }
    if (restoreConsoleVersion) {
      this.orderItem.consoleVersion = new ARConsoleVersion();
      this.orderItem.consoleVersion.id = null;
    }
    if (restoreFertilizerType) {
      this.orderItem.fertilizerType = new ARFertilizerType();
      this.orderItem.fertilizerType.id = null;
    }

    if (restoreFallFirstOrganicProductType) {
      this.orderItem.fallFirstOrganicProductType = new ARColzaOrganicProductType();
      this.orderItem.fallFirstOrganicProductType.id = null;
    }
    if (restoreFallSecondOrganicProductType) {
      this.orderItem.fallSecondOrganicProductType = new ARColzaOrganicProductType();
      this.orderItem.fallSecondOrganicProductType.id = null;
    }
    if (restorePastAzoteFrequency) {
      this.orderItem.pastAzoteFrequency = new ARColzaAzoteFrequency();
      this.orderItem.pastAzoteFrequency.id = null;
    }
    if (restoreOrderType) {
      this.orderItem.orderType = new ARColzaOrderType();
      this.orderItem.orderType.id = null;
    }
    if (restorePastAzoteProductType) {
      this.orderItem.pastAzoteProductType = new ARColzaAzoteProductType();
      this.orderItem.pastAzoteProductType.id = null;
    }
  }

  getLists() {
    const listTypes = [
      ARColzaListType.COLZA_ORGANIC_PRODUCT_TYPES,
      ARColzaListType.COLZA_AZOTE_PRODUCT_TYPES,
      ARColzaListType.COLZA_AZOTE_FREQUENCIES,
      ARColzaListType.COLZA_ORDER_TYPES,
    ];
    return this.colzaListService.getListTypes(ARColzaListType, ...listTypes);
  }

  handleLists(lists: Map<ARColzaListType, ListItem[]>) {
    this.azoteProductTypes = lists.get(ARColzaListType.COLZA_AZOTE_PRODUCT_TYPES).sort(sortingString('label'));
    for (const p of this.azoteProductTypes) {
      this.productTypesIndex.set(p.id, p.code);
    }
    this.pastAzoteProductTypes = this.azoteProductTypes.filter((p) => p.code !== 'OTHER');
    this.azoteFrequencies = lists.get(ARColzaListType.COLZA_AZOTE_FREQUENCIES).sort(sortingNumber('id'));

    this.organicProductTypes = lists.get(ARColzaListType.COLZA_ORGANIC_PRODUCT_TYPES).sort(sortingString('label'));
    for (const p of this.organicProductTypes) {
      this.organicProductTypesIndex.set(p.id, p.code);
    }

    this.orderTypes = lists.get(ARColzaListType.COLZA_ORDER_TYPES).sort(sortingNumber('id'));
    for (const p of this.orderTypes) {
      this.orderTypesIndex.set(p.id, p.code);
      this.orderTypesReverseIndex.set(p.code, p.id);
    }

    this.firstOrganicProductTypes = this.organicProductTypes.filter((p) => p.code !== 'OTHER');
    this.secondOrganicProductTypes = this.organicProductTypes.filter((p) => p.code !== 'OTHER');
  }

  ngOnInit() {
    this.sub = this.route.params.subscribe((params) => {
      if (this.route.snapshot.url[4].path === 'edit') {
        if (params['parcelId']) {
          const parcelId = +params['parcelId'];
          const obs0 = this.colzaService.getParcel(parcelId);
          const obs1 = this.beApiService.getOrderItemByParcel(parcelId);

          forkJoin(obs0, obs1).subscribe((data: any) => {
            const baOI = data[1];
            if (baOI) {
              this.hasBeApi = baOI.hasBeApi;
            }
            // besoin que si pas en batch
            this.getOrganisation().subscribe((organisation) => {
              this.handleShowBeApi(organisation);
            });

            const parcel = data[0];
            if (parcel.orderItem) {
              this.orderItemId = parcel.orderItem.id;
              this.edited = true;
              // FIXME traiter les erreurs ; afficher un message
              const obs1 = this.colzaService.getOrderItem(this.orderItemId);
              const obs2 = this.getLists();
              const obs3 = this.colzaService.getCampaigns();
              forkJoin(obs1, obs2, obs3).subscribe(
                (data: Array<any>) => {
                  this.orderItem = data[0];
                  this.parcel = this.orderItem.parcel;
                  this.updateSaveForbidden();
                  this.handleLists(data[1]);
                  this.prepareOrderItem(this.orderItem);
                  const campaigns = data[2];
                  this.handleCampaigns(campaigns);
                },
                (e) => this.handleGetError(e),
              );
            } else {
              this.parcel = parcel;
              this.edited = false;
              this.getLists().subscribe((lists: Map<ARColzaListType, ListItem[]>) => {
                this.handleLists(lists);
              });
              this.newOrderItem(parcelId);
              this.updateSaveForbidden();
              const obs2 = this.colzaService.getCampaigns();
              obs2.subscribe((campaigns: any) => {
                this.handleCampaigns(campaigns);
              });
            }
          });
        } else {
          showErrorMessage(this.modal, this.i18nTranslator.translate('ar.edit.error.pasDeParcelle')).then((r) => {
            this.router.navigate(['/orders', 'azote', 'colza', 'parcels']);
          });
        }
      } else if (this.route.snapshot.url[4].path === 'batch') {
        this.isBatch = true;
        this.edited = true;
        const parcelIds = params['parcelIds'].split('-').map((x: string) => +x);

        this.getLists().subscribe((lists: Map<ARColzaListType, ListItem[]>) => {
          this.handleLists(lists);
          getBatchOrderItems(
            this.parcelService,
            this.colzaService,
            parcelIds,
            this,
            'ARColza',
            () => {
              return new ARColzaOrderItem();
            },
            () => {
              return new ARColzaParcel();
            },
            ['azote', 'colza'],
            () => {
              this.cbBatchEditEh = new CbBatchEditEH(this.cbBatchEdit);
              this.updateSaveForbidden();
            },
          );
        });
        this.colzaService.getCampaigns().subscribe((campaigns: any[]) => {
          this.handleCampaigns(campaigns);
        });
      }
    });
  }

  newOrderItem(parcelId: number) {
    this.orderItem = new ARColzaOrderItem();
    this.orderItem.parcel = new ARColzaParcel();

    this.orderItem.parcel.id = parcelId;

    this.orderItem.pastAzoteProductType = new ARColzaAzoteProductType();
    this.orderItem.pastAzoteProductType.id = null;

    this.orderItem.pastAzoteFrequency = new ARColzaAzoteFrequency();
    this.orderItem.pastAzoteFrequency.id = null;

    this.orderItem.orderType = new ARColzaOrderType();
    this.orderItem.orderType.id = null;

    this.orderItem.fallFirstOrganicProductType = new ARColzaOrganicProductType();
    this.orderItem.fallFirstOrganicProductType.id = null;

    this.orderItem.fallSecondOrganicProductType = new ARColzaOrganicProductType();
    this.orderItem.fallSecondOrganicProductType.id = null;

    this.orderItem.hasMicroParcels = null;
    this.orderItem.hasCouvertLegumineuses = null;
    this.orderItem.hasReliquatSortieHiver = null;
    this.orderItem.reliquatSortieHiver = null;

    this.prepareFertilizerData();
    this.prepareVraData();
    this.computeAdviceTypeLock();
  }

  getParcel(parcelId: number) {
    this.colzaService.getParcel(parcelId).subscribe(
      (parcel) => {
        this.parcel = parcel;
      },
      (error) => this.handleGetError(error),
    );
  }

  prepareOrderItem(orderItem: ARColzaOrderItem) {
    if (!this.orderItem.orderType) {
      this.orderItem.orderType = new ARColzaOrderType();
      this.orderItem.orderType.id = null;
    } else {
      this.onChangeOrderType(this.orderItem.orderType.id);
    }

    if (!this.orderItem.pastAzoteProductType) {
      this.orderItem.pastAzoteProductType = new ARColzaAzoteProductType();
      this.orderItem.pastAzoteProductType.id = null;
    }

    // onChangePastAzoteFrequency a besoin du pastAzoteProductType initalisé
    if (!this.orderItem.pastAzoteFrequency) {
      this.orderItem.pastAzoteFrequency = new ARColzaAzoteFrequency();
      this.orderItem.pastAzoteFrequency.id = null;
    } else {
      this.onChangePastAzoteFrequency(this.orderItem.pastAzoteFrequency.id);
    }

    if (!this.orderItem.fallFirstOrganicProductType) {
      this.orderItem.fallFirstOrganicProductType = new ARColzaOrganicProductType();
      this.orderItem.fallFirstOrganicProductType.id = null;
    } else {
      this.onChangeFallFirstOrganicProductType(this.orderItem.fallFirstOrganicProductType.id);
    }

    if (!this.orderItem.fallSecondOrganicProductType) {
      this.orderItem.fallSecondOrganicProductType = new ARColzaOrganicProductType();
      this.orderItem.fallSecondOrganicProductType.id = null;
    } else {
      this.onChangeFallSecondOrganicProductType(this.orderItem.fallSecondOrganicProductType.id);
    }

    if (!isBooleanValue(this.orderItem.hasCouvertLegumineuses)) {
      this.orderItem.hasCouvertLegumineuses = null;
    }

    if (!isBooleanValue(this.orderItem.hasReliquatSortieHiver)) {
      this.orderItem.hasReliquatSortieHiver = false;
    } else {
      this.onChangeNeedsReliquatSortieHiver(this.orderItem.hasReliquatSortieHiver);
    }

    if (!isBooleanValue(this.orderItem.hasMicroParcels)) {
      this.orderItem.hasMicroParcels = null;
    }

    if (!isBooleanValue(this.orderItem.needsCarteEpandage)) {
      this.orderItem.needsCarteEpandage = false;
    } else {
      this.onChangeNeedsCarteEpandage(this.orderItem.needsCarteEpandage);
    }

    this.prepareFertilizerData();
    this.prepareVraData();
    this.computeAdviceTypeLock();
  }

  prepareFertilizerData() {
    this.ehFertilizerData = new ARFertilizerEHDataFacade(this.orderItem);
  }

  prepareVraData() {
    this.ehVraData = new ARVraEHDataFacade(this.orderItem);
  }

  private computeAdviceTypeLock(): void {
    if (this.isAdmin) {
      this.isAdviceTypeLocked = false;
      return;
    }

    const items = this.isBatch ? this.batchOrderItems : [this.orderItem];
    if (!items || items.length === 0) {
      this.isAdviceTypeLocked = false;
      return;
    }

    const dates = this.collectRestitutionDatesFromOrderItems(items);
    if (dates.length === 0) {
      this.isAdviceTypeLocked = false;
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    this.isAdviceTypeLocked = dates.some((date) => date.getTime() - today.getTime() < this.adviceTypeLockThresholdMs);
  }

  private collectRestitutionDatesFromOrderItems(items: ARColzaOrderItem[]): Date[] {
    const dates: Date[] = [];
    const seen = new Set<number>();

    items
      .filter((item) => !!item && !!item.order)
      .forEach((item) => {
        const campaignYear = this.getCampaignYearFromOrderItem(item);
        const candidates = [
          this.normalizeRestitutionDate(item.eh_date_livraison, campaignYear),
          this.normalizeRestitutionDate(item.sh_date_livraison, campaignYear),
          this.normalizeRestitutionDate(item.defaultWeek, campaignYear),
        ];

        candidates
          .filter((date) => !!date)
          .forEach((date) => {
            const time = date.getTime();
            if (!seen.has(time)) {
              seen.add(time);
              dates.push(date);
            }
          });
      });

    return dates;
  }

  private normalizeRestitutionDate(date: any, campaignYear?: number): Date {
    if (!date) {
      return null;
    }

    if (date instanceof Date) {
      const parsed = new Date(date.getTime());
      parsed.setHours(0, 0, 0, 0);
      const corrected = correctDate(parsed);
      corrected.setHours(0, 0, 0, 0);
      return corrected;
    }

    if (typeof date === 'string' || typeof date === 'number') {
      const parsed = new Date(date as any);
      if (isNaN(parsed.getTime())) {
        return null;
      }
      const corrected = correctDate(parsed);
      corrected.setHours(0, 0, 0, 0);
      return corrected;
    }

    if (typeof date === 'object') {
      const defaultWeekDate = this.normalizeDefaultWeek(date, campaignYear);
      if (defaultWeekDate) {
        return defaultWeekDate;
      }

      if (date.date) {
        return this.normalizeRestitutionDate(date.date, campaignYear);
      }

      return null;
    }

    return null;
  }

  private normalizeDefaultWeek(value: any, campaignYear?: number): Date {
    if (!value || typeof value.week === 'undefined') {
      return null;
    }

    const week = this.parseWeekNumber(value.week);
    if (!week || week <= 0) {
      return null;
    }

    let year = this.extractYearValue(campaignYear);
    if (!year) {
      year = this.extractYearValue(value.year);
    }

    if (!year) {
      return null;
    }

    const weekDate = this.getDateForIsoWeek(year, week);
    if (!weekDate) {
      return null;
    }

    const corrected = correctDate(weekDate);
    corrected.setHours(0, 0, 0, 0);
    return corrected;
  }

  private getDateForIsoWeek(year: number, week: number): Date {
    if (!year || !week || week < 1) {
      return null;
    }

    const januaryFourth = new Date(year, 0, 4);
    const januaryFourthDay = januaryFourth.getDay() || 7;
    const weekStart = new Date(januaryFourth.getTime());
    weekStart.setDate(januaryFourth.getDate() - januaryFourthDay + 1 + (week - 1) * 7);
    weekStart.setHours(0, 0, 0, 0);
    return weekStart;
  }

  private extractYearValue(value: any): number {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === 'number') {
      return isNaN(value) ? null : value;
    }

    if (value instanceof Date) {
      const copy = new Date(value.getTime());
      return isNaN(copy.getTime()) ? null : copy.getUTCFullYear();
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed === '') {
        return null;
      }

      const parsedDate = new Date(trimmed);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.getUTCFullYear();
      }

      const numeric = parseInt(trimmed, 10);
      return isNaN(numeric) ? null : numeric;
    }

    if (typeof value === 'object') {
      if (typeof value.year !== 'undefined') {
        return this.extractYearValue(value.year);
      }
      if (typeof value.campaignYear !== 'undefined') {
        return this.extractYearValue(value.campaignYear);
      }
    }

    return null;
  }

  private parseWeekNumber(value: any): number {
    if (typeof value === 'number') {
      return isNaN(value) ? null : Math.floor(value);
    }
    if (typeof value === 'string') {
      const parsed = parseInt(value, 10);
      return isNaN(parsed) ? null : parsed;
    }
    return null;
  }

  private getCampaignYearFromOrderItem(orderItem: ARColzaOrderItem): number {
    if (!orderItem || !orderItem.order || !orderItem.order.campaign) {
      return null;
    }

    const campaign = orderItem.order.campaign;

    let year = this.extractYearValue(campaign.campaignYear);
    if (!year) {
      year = this.extractYearValue(campaign.year);
    }

    if (!year) {
      year = this.extractYearValue(campaign.endingOn);
    }

    if (!year) {
      year = this.extractYearValue(campaign.startingOn);
    }

    if (!year && campaign.configuration) {
      const config = campaign.configuration;
      const candidates = [config.startingOn, config.endingOnEh, config.endingOnSh];

      for (const candidate of candidates) {
        year = this.extractYearValue(candidate);
        if (year) {
          break;
        }
      }
    }

    return year;
  }

  // FIXME récupérer la campagne avec la parcelle (au lieu de séparément)
  handleCampaigns(campaigns: ARColzaCampaign[]) {
    campaigns = campaigns.filter((x) => ARColzaUtils.isCampaignCurrentlyOpen(x));
    if (this.isBatch) {
      // on suppose qu'on est dans la dernière campagne
      // TODO  vérifier avec les parcelles; normalement pas éditable sinon
      if (campaigns.length > 0) {
        this.currentCampaign = campaigns[0];
        this.isColzaEHEnded = ARColzaUtils.isColzaEHEnded(this.currentCampaign);
      }
    } else {
      for (const campaign of campaigns) {
        if (campaign.id === this.parcel.campaign.id) {
          this.currentCampaign = campaign;
          this.isColzaEHEnded = ARColzaUtils.isColzaEHEnded(campaign);
        }
      }
    }
  }

  addOrder() {
    this.withinOrder = true;
    this.save();
  }

  confirmOrder(parcelId: number) {
    this.router.navigate(['/orders', 'azote', 'colza', 'orders', 'new', parcelId]);
  }

  onChangePastAzoteFrequency(azoteFrequencyId: any) {
    if (!azoteFrequencyId) {
      return;
    }
    azoteFrequencyId = +azoteFrequencyId;
    const azoteFrequency = this.azoteFrequencies.filter((f) => f.id === azoteFrequencyId)[0];
    const noneProductType = this.azoteProductTypes.filter((productType) => productType.code === 'NONE')[0];
    if (azoteFrequency.code === 'NONE') {
      this.pastAzoteProductTypes = [noneProductType];
      this.orderItem.pastAzoteProductType.id = noneProductType.id;
    } else {
      this.pastAzoteProductTypes = this.azoteProductTypes.filter((p) => p.code !== 'NONE' && p.code !== 'OTHER');
      if (this.orderItem.pastAzoteProductType && this.orderItem.pastAzoteProductType.id === noneProductType.id) {
        this.orderItem.pastAzoteProductType.id = null;
      }
    }
  }

  onChangeFallFirstOrganicProductType(productTypeId: any) {
    if (!productTypeId) {
      return;
    }
    productTypeId = +productTypeId;
    const code = this.organicProductTypesIndex.get(productTypeId);

    if (code === 'NONE') {
      this.orderItem.fallFirstOrganicConcentration = undefined;
      this.orderItem.fallFirstOrganicDensity = undefined;
      this.showFallFirstOrganicInformation = false;
    } else {
      this.showFallFirstOrganicInformation = true;
      this.requireFallFirstOrganicInformation = code === 'OTHER';
    }
  }

  onChangeFallSecondOrganicProductType(productTypeId: any) {
    if (!productTypeId) {
      return;
    }
    productTypeId = +productTypeId;
    const code = this.organicProductTypesIndex.get(productTypeId);

    if (code === 'NONE') {
      this.orderItem.fallSecondOrganicConcentration = undefined;
      this.orderItem.fallSecondOrganicDensity = undefined;
      this.showFallSecondOrganicInformation = false;
    } else {
      this.showFallSecondOrganicInformation = true;
      this.requireFallSecondOrganicInformation = code === 'OTHER';
    }
  }

  onChangeOrderType(orderTypeId: any) {
    if (!orderTypeId) {
      return;
    }
    orderTypeId = +orderTypeId;
    if (this.isAdviceTypeLocked) {
      return;
    }
    const code = this.orderTypesIndex.get(orderTypeId);

    if (code === 'SH' || code == 'EH_SH') {
      // zonage
      this.onChangeOrderTypeOffre(1);
    } else {
      // sans zonage
      this.onChangeOrderTypeOffre(2);
    }
  }

  onChangeOrderTypeOffre(orderType_index: any) {
    if (this.isAdviceTypeLocked) {
      return;
    }

    this.isZonageOrderType = orderType_index == 1;

    if (this.orderItem.hasCouvertLegumineuses != null && this.orderItem.hasCouvertLegumineuses != undefined) {
      this.commitOrderType(this.orderItem.hasCouvertLegumineuses);
    }
  }

  commitOrderType(hasCouvertLegumineuses: any) {
    // sinon la valeur est une chaine eg "true" si a été changé dans l'UI
    // TODO voir si on peut faire mieux ???
    hasCouvertLegumineuses = toBoolean(hasCouvertLegumineuses);

    let code: string;
    if (this.isZonageOrderType && hasCouvertLegumineuses) {
      code = 'SH';
    } else if (this.isZonageOrderType && !hasCouvertLegumineuses) {
      code = 'EH_SH';
    } else if (!this.isZonageOrderType && hasCouvertLegumineuses) {
      code = 'SH_SIMPLIFIE';
    } else {
      code = 'SIMPLIFIE';
    }

    const orderTypeId = this.orderTypesReverseIndex.get(code);
    this.orderItem.orderType.id = orderTypeId;

    let isNewZonageEH = false;
    let isNewZonageSH = false;

    if (code == 'SIMPLIFIE' || code == 'SH_SIMPLIFIE') {
      this.isZonageEH = false;
      this.isZonageSH = false;
    } else if (code == 'EH_SH') {
      // ne pas changer si déja vrai
      isNewZonageEH = this.isZonageEH === false;
      isNewZonageSH = this.isZonageSH === false;

      this.isZonageEH = true;
      this.isZonageSH = true;
    } else if (code == 'SH') {
      // ne pas changer si déja vrai
      isNewZonageSH = this.isZonageSH === false;

      this.isZonageEH = false;
      this.isZonageSH = true;
    }

    if (isNewZonageEH) {
      this.prepareNewZonageEH();
    }

    if (isNewZonageSH) {
      this.prepareNewZonageSH();
    }

    if (!this.isZonageEH && !this.isZonageSH) {
      // simplifie
      this.requireEpandageInfo = null;
      this.orderItem.needsCarteEpandage = false;
      this.orderItem.ehAzoteToSpread = null;
      this.eraseZonageEH();
      this.eraseZonageSH();
    }
  }

  isOrderTypeFilled() {
    if (!this.isBatch) {
      return !!(this.orderItem.orderType && this.orderItem.orderType.id);
    }

    if (this.orderItem.orderType && this.orderItem.orderType.id) {
      // rempli explicitement ou meme pour tous si edition
      return true;
    }

    let isOK: boolean = true;
    this.batchOrderItems.forEach((x) => {
      if (!x.id) {
        // si pas encore créé, le orderType de l'UI doit être remplie (s'appliquera à tous)
        // mais condition deja testee : pas le cas donc erreur
        isOK = false;
      }
    });

    return isOK;
  }

  eraseZonageEH() {
    this.orderItem.ehFertilizerType = new ARFertilizerType();
    this.orderItem.ehFertilizerConcentration = null;
    this.orderItem.ehConsoleModulationType = new ARConsoleModulationType();
    this.orderItem.ehConsoleType = new ARConsoleType();
    this.orderItem.ehConsoleVersion = new ARConsoleVersion();
    this.orderItem.ehOtherConsoleVersion = null;
    this.orderItem.ehConsoleFormatType = new ARConsoleFormatType();
  }

  eraseZonageSH() {
    this.orderItem.fertilizerType = new ARFertilizerType();
    this.orderItem.fertilizerConcentration = null;
    this.orderItem.consoleModulationType = new ARConsoleModulationType();
    this.orderItem.consoleType = new ARConsoleType();
    this.orderItem.consoleVersion = new ARConsoleVersion();
    this.orderItem.otherConsoleVersion = null;
    this.orderItem.consoleFormatType = new ARConsoleFormatType();
  }

  prepareNewZonageEH(orderItem: ARColzaOrderItem = this.orderItem, value: any = null) {
    orderItem.ehFertilizerConcentration = value;
    orderItem.ehFertilizerType = value;

    orderItem.ehConsoleFormatType = value;
    orderItem.ehConsoleType = value;
    orderItem.ehConsoleVersion = value;
    orderItem.ehConsoleModulationType = value;
    orderItem.ehOtherConsoleVersion = value;
  }

  prepareNewZonageSH(orderItem: ARColzaOrderItem = this.orderItem, value: any = null) {
    orderItem.fertilizerConcentration = value;
    orderItem.fertilizerType = value;

    orderItem.consoleFormatType = value;
    orderItem.consoleType = value;
    orderItem.consoleVersion = value;
    orderItem.consoleModulationType = value;
    orderItem.otherConsoleVersion = value;
  }

  onChangeNeedsReliquatSortieHiver(needsReliquat: any) {
    needsReliquat = toBoolean(needsReliquat);

    this.requireReliquatSortieHiver = needsReliquat;
    if (!needsReliquat) {
      this.orderItem.reliquatSortieHiver = null;
    }
  }

  onChangeCouvertLegumineuses(hasCouvertLegumineuses: any) {
    if (this.isAdviceTypeLocked) {
      return;
    }

    if (this.isZonageOrderType != null) {
      this.commitOrderType(hasCouvertLegumineuses);
    }
  }

  hasCouvertLegumineuses() {
    const _hasCouvertLegumineuses = toBoolean(this.orderItem.hasCouvertLegumineuses);
    return _hasCouvertLegumineuses;
  }

  onChangeEhFertilizer(isEvent: boolean = true) {
    // seulement si pas déjà rempli
    if (!this.orderItem.fertilizerConcentration || this.ehShLinks.get('fertilizerConcentration')) {
      if (!this.isBatch || (<any>this.cbBatchEdit)['fertilizerConcentration']) {
        this.orderItem.fertilizerConcentration = this.orderItem.ehFertilizerConcentration;
        this.ehShLinks.set('fertilizerConcentration', true);
      }
    }
    if (
      this.orderItem.ehFertilizerType &&
      this.orderItem.ehFertilizerType.id &&
      (!this.orderItem.fertilizerType || !this.orderItem.fertilizerType.id || this.ehShLinks.get('fertilizerType'))
    ) {
      if (!this.isBatch || (<any>this.cbBatchEdit)['fertilizerType']) {
        this.orderItem.fertilizerType = new ARFertilizerType();
        this.orderItem.fertilizerType.id = this.orderItem.ehFertilizerType.id;
        this.ehShLinks.set('fertilizerType', true);
      }
    }
  }

  onChangeShFertilizer(isEvent: boolean = true) {
    if (isEvent) {
      // changé manuellement => changer eh ne va plus changer sh
      // Note : sh n'a pas le préfixe SH
      this.ehShLinks.set('fertilizerConcentration', false);
      this.ehShLinks.set('fertilizerType', false);
    }
  }

  onChangeEhVra(isEvent: boolean = true) {
    if (
      (this.orderItem.ehConsoleModulationType &&
        this.orderItem.ehConsoleModulationType.id &&
        (!this.orderItem.consoleModulationType || !this.orderItem.consoleModulationType.id)) ||
      this.ehShLinks.get('consoleModulationType')
    ) {
      if (!this.isBatch || (<any>this.cbBatchEdit)['consoleModulationType']) {
        this.orderItem.consoleModulationType = new ARConsoleModulationType();
        this.orderItem.consoleModulationType.id = this.orderItem.ehConsoleModulationType.id;
        this.ehShLinks.set('consoleModulationType', true);
        if (this.vraEditSHRef.first) {
          this.vraEditSHRef.first.onChangeConsoleModulationType(this.orderItem.consoleModulationType.id);
          // va changer les autres champs aussi => on les met dans links
          this.ehShLinks.set('consoleType', true);
          this.ehShLinks.set('consoleVersion', true);
          this.ehShLinks.set('consoleFormatType', true);
          this.ehShLinks.set('otherConsoleVersion', true);
        }
      }
    }
    if (
      (this.orderItem.ehConsoleType &&
        this.orderItem.ehConsoleType.id &&
        (!this.orderItem.consoleType || !this.orderItem.consoleType.id)) ||
      this.ehShLinks.get('consoleType')
    ) {
      if (!this.isBatch || (<any>this.cbBatchEdit)['consoleType']) {
        this.orderItem.consoleType = new ARConsoleType();
        this.orderItem.consoleType.id = this.orderItem.ehConsoleType.id;
        this.ehShLinks.set('consoleType', true);
      }
    }
    if (
      (this.orderItem.ehConsoleVersion &&
        this.orderItem.ehConsoleVersion.id &&
        (!this.orderItem.consoleVersion || !this.orderItem.consoleVersion.id)) ||
      this.ehShLinks.get('consoleVersion')
    ) {
      if (!this.isBatch || (<any>this.cbBatchEdit)['consoleVersion']) {
        this.orderItem.consoleVersion = new ARConsoleVersion();
        this.orderItem.consoleVersion.id = this.orderItem.ehConsoleVersion.id;
        this.ehShLinks.set('consoleVersion', true);
      }
    }
    if (
      (this.orderItem.ehConsoleFormatType &&
        this.orderItem.ehConsoleFormatType.id &&
        (!this.orderItem.consoleFormatType || !this.orderItem.consoleFormatType.id)) ||
      this.ehShLinks.get('consoleFormatType')
    ) {
      if (!this.isBatch || (<any>this.cbBatchEdit)['consoleFormatType']) {
        this.orderItem.consoleFormatType = new ARConsoleFormatType();
        this.orderItem.consoleFormatType.id = this.orderItem.ehConsoleFormatType.id;
        this.ehShLinks.set('consoleFormatType', true);
      }
    }

    if (!this.orderItem.otherConsoleVersion || this.ehShLinks.get('otherConsoleVersion')) {
      if (!this.isBatch || (<any>this.cbBatchEdit)['otherConsoleVersion']) {
        this.orderItem.otherConsoleVersion = this.orderItem.ehOtherConsoleVersion;
        this.ehShLinks.set('otherConsoleVersion', true);
      }
    }
  }

  onChangeShVra(isEvent: boolean = true) {
    if (isEvent) {
      this.ehShLinks.set('consoleFormatType', false);
      this.ehShLinks.set('consoleType', false);
      this.ehShLinks.set('consoleVersion', false);
      this.ehShLinks.set('consoleModulationType', false);
      this.ehShLinks.set('otherConsoleVersion', false);
    }
  }

  onChangeNeedsCarteEpandage(needsCarteEpandage: any) {
    needsCarteEpandage = toBoolean(needsCarteEpandage);
    this.orderItem.needsCarteEpandage = needsCarteEpandage;
    this.requireEpandageInfo = needsCarteEpandage;

    if (!needsCarteEpandage) {
      this.prepareCarteEpandage();
    }
  }

  prepareCarteEpandage() {
    this.orderItem.ehFertilizerType = new ARFertilizerType();
    this.orderItem.ehFertilizerType.id = null;
    this.orderItem.ehFertilizerConcentration = null;

    this.orderItem.ehConsoleModulationType = new ARConsoleModulationType();
    this.orderItem.ehConsoleModulationType.id = null;
    this.orderItem.ehConsoleType = new ARConsoleType();
    this.orderItem.ehConsoleType.id = null;
    this.orderItem.ehConsoleFormatType = new ARConsoleFormatType();
    this.orderItem.ehConsoleFormatType.id = null;
    this.orderItem.ehConsoleVersion = new ARConsoleVersion();
    this.orderItem.ehConsoleVersion.id = null;
    this.orderItem.ehOtherConsoleVersion = null;
  }

  getOrganisation(): Observable<Organisation> {
    return this.userService.getMe().pipe(map((me) => me.organisation));
  }

  handleShowBeApi(organisation: Organisation) {
    // TODO quand i18n mergée : récupérer l'org depuis la structure
    const isAdmin = KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN');
    const isNegoce = KeycloakService.auth.authz.hasRealmRole('ROLE_NEGOCE');
    if (isAdmin || isNegoce) {
      this.showBeApi = true;
      return;
    }

    this.showBeApi = organisation.hasBeApi;
  }

  handleSaveError(error: any) {
    if (isFarmerActionForbidden(error)) {
      showErrorMessage(this.modal, this.i18nTranslator.translate('error.farmerActionForbidden'));
    } else if (error.status === 401) {
      showErrorMessage(this.modal, this.i18nTranslator.translate('"ar.edit.error.unauthorized.edit'));
    } else {
      // FIXME détailler les erreurs du formulaire
      // FIXME définir un format pour les erreurs de validation
      console.error(error);
      showErrorMessage(this.modal, this.i18nTranslator.translate('edit.error.generic'));
    }
  }

  handleGetError(error: any) {
    if (error.status === 401) {
      showErrorMessage(this.modal, this.i18nTranslator.translate('"ar.edit.error.unauthorized.view')).then((r) => {
        this.router.navigate(['/orders', 'azote', 'colza', 'parcels']);
      });
    } else {
      console.error(error);
      showErrorMessage(this.modal, this.i18nTranslator.translate('error.generic')).then((r) => {
        this.router.navigate(['/orders', 'azote', 'colza', 'parcels']);
      });
    }
  }

  handleSavebatchError(error: any) {
    if (error.status === 401) {
      showErrorMessage(this.modal, this.i18nTranslator.translate('ar.edit.error.unauthorized.edit.multiParcels'));
    } else {
      // FIXME détailler les erreurs du formulaire
      // FIXME définir un format pour les erreurs de validation
      console.error(error);
      showErrorMessage(this.modal, this.i18nTranslator.translate('ar.edit.error.save.multiParcels'));
    }
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }
}
